import type { AxiosError } from 'axios';
import { isAxiosError } from 'axios';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    ApiRequestError,
    callApi,
    describeApiFailure,
    errorMessageFromResponse,
    NetworkRequestState,
} from 'c-data-layer';
import type { UnPromisify } from 'c-types';
import type { AxiosRequestData } from 'c-sdk';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
type ReqType<U> = (...args: Parameters<U>) => AxiosRequestData<U>;

function useAPIClientRequest<U, T extends ReqType<U>>(apiRequest: T) {
    type Data = UnPromisify<ReturnType<T>['request']>;
    const requestStateRef = useRef<NetworkRequestState>(NetworkRequestState.Idle);
    const dataRef = useRef<Data | null>(null);

    const [requestState, setRequestState] = useState<NetworkRequestState>(NetworkRequestState.Idle);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<Data | null>(null);
    const dispatch = useDispatch();

    const abortControllerRef = useRef<AbortController | null>(null);

    const start = useCallback(
        async (...args: Parameters<T>) => {
            if (requestStateRef.current === NetworkRequestState.InProgress) {
                // return dataRef.current;
                abortControllerRef?.current?.abort();
            }

            setError(null);
            setRequestState(NetworkRequestState.InProgress);
            requestStateRef.current = NetworkRequestState.InProgress;

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const { request, controller } = apiRequest(...args);

            abortControllerRef.current = controller;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const [err, _, success]: [AxiosError, U, (typeof dataRef)['current']] = await dispatch(
                callApi(
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    request,
                ),
            );

            if (!err && success != null) {
                requestStateRef.current = NetworkRequestState.Success;
                setRequestState(NetworkRequestState.Success);

                dataRef.current = success;
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                setData(success);
            } else if (err) {
                dataRef.current = null;
                setData(null);

                setRequestState(NetworkRequestState.Error);
                requestStateRef.current = NetworkRequestState.Error;
                const errorString = String(errorMessageFromResponse(err as any) ?? '');
                setError(errorString);

                // Same message as before; the request details ride along for Sentry (c-data sentry.ts).
                throw isAxiosError(err)
                    ? new ApiRequestError(errorString, describeApiFailure(err))
                    : Error(errorString);
            }

            return dataRef.current;
        },
        [dispatch, apiRequest],
    );

    const reset = useCallback(() => {
        setRequestState(NetworkRequestState.Idle);
        setError(null);
        setData(null);
    }, []);

    return useMemo(
        () => ({
            requestState,
            start,
            error,
            data,
            reset,
            isIdle: requestState === NetworkRequestState.Idle,
            isLoading: requestState === NetworkRequestState.InProgress,
            hasFailed: requestState === NetworkRequestState.Error,
            hasSucceeded: requestState === NetworkRequestState.Success,
            abortControllerRef,
            requestStateRef,
        }),
        [data, error, requestState, reset, start],
    );
}

export default useAPIClientRequest;
